import React, { useState } from 'react';
import './Table.scss';

type columnsType = {
  header: string;
  accessor: string;
  sortable: boolean;
  cell: (row: any) => JSX.Element;
  onCellClick?: (value:any,accessor:any,row:any,index:number) => void;
  cellClassName?: (row: any) => string;
};

type ExpandedRow = {
  index: number | null;
  row: any | null;
};

type actionsIconsType = {
  icon: string;
  onClick: (action:string,row: any,index:number) => void;
  tooltip: string;
  name: string;
};

type TableProps = {
  // Define your table props here
  data: any;
  columns: columnsType[];
  isExpandable?: boolean;
  onRowExpand?: (row: any) => void;
  sorting?: {
    column: string;
    direction: 'asc' | 'desc' | '';
  };
  onSortingChange?: (column: string, direction: 'asc' | 'desc' | '') => void;
  renderRowSubComponent?: (row: any) => void;
  actionIcons?: actionsIconsType[] | ((row: any) => actionsIconsType[]);
};

const Table = ({
  data = [], // Default to an empty array if no data is provided
  columns = [], // Default to an empty array if no columns are provided
  isExpandable = false,
  onRowExpand,
  sorting,
  onSortingChange,
  renderRowSubComponent = () => <></>,
  actionIcons = []
}: TableProps) => {
  const [expandedRow, setExpandedRow] = useState<[ExpandedRow]>([
    { index: null, row: null }
  ]);

  // Helper function to get action icons for a specific row
  const getRowActionIcons = (row: any) => {
    if (typeof actionIcons === 'function') {
      return actionIcons(row);
    }
    return actionIcons;
  };

  // Helper function to check if any row has action icons
  const hasActionIcons = () => {
    if (typeof actionIcons === 'function') {
      // For function type, we need to check if any row would have action icons
      // We'll assume there are action icons if the function is provided
      return data.some((row: any) => getRowActionIcons(row).length > 0);
    }
    return Array.isArray(actionIcons) && actionIcons.length > 0;
  };

  const handleRowExpand = (row: any, index: number) => {
    if (expandedRow[0].index === index) {
      setExpandedRow([{ index: null, row: null }]);
    } else {
      setExpandedRow([{ index, row: renderRowSubComponent(row) }]);
    }
    if (onRowExpand) {
      onRowExpand(row);
    }
  };

  const getSortingIcon = (column: string) => {
    if (sorting && sorting.column === column) {
      return sorting.direction === 'asc' ? (
        <span className="icon-16-arrow-up-mono sort-asc" />
      ) : (
        <span className="icon-16-arrow-down-mono sort-desc" />
      );
    }
    return null;
  };

  const handleSortingClick = (column: string) => {
    if (onSortingChange) {
      const sortingColumn=sorting && sorting.column === column && sorting.direction === 'desc' ? '' : column;
      let newDirection: 'asc' | 'desc' | '' = '';
      if(sortingColumn === ''){
        newDirection='';
      }
      else if( sorting && sorting.column === column && sorting.direction === 'asc'){
        newDirection = 'desc';
      }
      else{
        newDirection = 'asc';
      }
      onSortingChange(sortingColumn, newDirection);
    }
  };

  if (data.length === 0) {
    return <div className="no-data">No data available</div>;
  }
  return (
    <div className="table-container">
      <table className="general-table">
        <thead>
          <tr className="table-row-header">
            {isExpandable && <th className="expandable"></th>}
            {columns.map((column) => (
              <th
                key={column.accessor}
                className="col-header"
                onClick={() => handleSortingClick(column.accessor)}>
                {column.header}
                {column.sortable && getSortingIcon(column.accessor)}
              </th>
            ))}
            {hasActionIcons() && (
              <th className="action-icons-header">Actions</th>
            )}
          </tr>
        </thead>
        <tbody className="table-body">
          {data.map((row: any, index: number) => (
            <>
              <tr className="table-row">
                {isExpandable && (
                  <td
                    className="expandable"
                    onClick={() => handleRowExpand(row, index)}>
                    {expandedRow[0].index === index ? (
                      <span className="icon-24-minus-mono"></span>
                    ) : (
                      <span className="icon-24-plus-mono"></span>
                    )}
                  </td>
                )}
                {columns.map((column) => (
                  <td
                    className={`table-col ${column.onCellClick ? 'clickable-col' : ''} ${column.cellClassName?.(row) || ''}` }
                    key={column.accessor}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (column.onCellClick) {
                        column.onCellClick(row[column.accessor],column.accessor, row,index);
                      }
                    }}>
                    {column.cell(row)}
                  </td>
                ))}

                {(() => {
                  const rowActionIcons = getRowActionIcons(row);
                  return rowActionIcons.length > 0 && (
                    <td>
                      <div className="action-icons">
                        {rowActionIcons.map((icon: any, idx: any) => (
                          <span
                            id={`action-icon-${icon.name}-${index}`}
                            key={idx}
                            title={icon?.tooltip}
                            className={icon.icon}
                            onClick={(e) => {
                              e.stopPropagation();
                              icon.onClick(icon.name,row,index);
                            }}></span>
                        ))}
                      </div>
                    </td>
                  );
                })()}
              </tr>
              {expandedRow[0].index === index && (
                <tr className="row-sub-component">
                  <td
                    colSpan={
                      columns.length +
                      (isExpandable ? 1 : 0) +
                      (hasActionIcons() ? 1 : 0)
                    }>
                    {expandedRow[0].row}
                  </td>
                </tr>
              )}
            </>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Table;
